import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {BASE_URL} from "../../config/config";

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.85);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    padding: 0;
    overflow: hidden;
    touch-action: none;

    @media (max-width: 768px) {
        background-color: rgba(0, 0, 0, 0.95);
        padding: 0;
    }
`;

const ModalContent = styled.div`
    position: relative;
    max-width: 90vw;
    max-height: 90vh;
    background-color: #1a1a1a;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
    touch-action: pan-y;

    @media (max-width: 768px) {
        max-width: 100vw;
        max-height: 100vh;
        width: 100%;
        height: 100%;
        border-radius: 0;
        padding: 10px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        overflow: hidden;
    }
`;

const CloseButton = styled.button`
    position: absolute;
    top: 10px;
    right: 10px;
    background: rgba(255, 255, 255, 0.2);
    border: none;
    color: white;
    font-size: 24px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s;
    z-index: 10;

    &:hover {
        background: rgba(255, 255, 255, 0.3);
    }

    &:active {
        background: rgba(255, 255, 255, 0.4);
    }

    @media (max-width: 768px) {
        width: 48px;
        height: 48px;
        font-size: 28px;
        top: 20px;
        right: 20px;
        background: rgba(0, 0, 0, 0.6);
        backdrop-filter: blur(4px);
    }
`;

const VideoContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;

    @media (max-width: 768px) {
        width: 100%;
        height: 100%;
    }
`;

const StyledVideo = styled.video`
    max-width: 85vw;
    max-height: 80vh;
    border-radius: 4px;

    @media (max-width: 768px) {
        max-width: 100%;
        max-height: 100%;
        width: 100%;
        height: auto;
        border-radius: 0;
    }
`;

const LoadingText = styled.p`
    color: white;
    text-align: center;
    padding: 40px;
`;

const SwipeIndicator = styled.div`
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    color: white;
    font-size: 14px;
    padding: 8px 16px;
    background: rgba(0, 0, 0, 0.7);
    border-radius: 20px;
    pointer-events: none;
    opacity: ${props => props.visible ? 1 : 0};
    transition: opacity 0.3s;
    z-index: 20;
    backdrop-filter: blur(4px);

    ${props => props.direction === 'up' && 'bottom: 80px;'}
    ${props => props.direction === 'down' && 'top: 80px;'}
`;

const VideoPlayerModal = ({linkId, onClose, onNext, onPrevious, hasNext, hasPrevious}) => {
    const [fileData, setFileData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    const [swipeIndicator, setSwipeIndicator] = useState({ visible: false, direction: null });

    useEffect(() => {
        const fetchFileData = async () => {
            try {
                const endpoint = `${BASE_URL}/public/meta/files/links/${linkId}`;
                const response = await fetch(endpoint);

                if (!response.ok) {
                    throw new Error(`Failed to fetch file metadata: ${response.status}`);
                }

                const data = await response.json();
                setFileData(data);
            } catch (error) {
                console.error("Error fetching file metadata:", error);
            } finally {
                setIsLoading(false);
            }
        };

        if (linkId) {
            fetchFileData();
        }
    }, [linkId]);

    // Prevent body scroll when modal is open
    useEffect(() => {
        const originalOverflow = document.body.style.overflow;
        const originalPosition = document.body.style.position;
        const originalWidth = document.body.style.width;

        // Prevent scrolling on the body
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';

        return () => {
            // Restore original styles when modal closes
            document.body.style.overflow = originalOverflow;
            document.body.style.position = originalPosition;
            document.body.style.width = originalWidth;
        };
    }, []);

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            onClose();
        } else if (e.key === 'ArrowUp' && hasNext) {
            e.preventDefault();
            onNext();
        } else if (e.key === 'ArrowDown' && hasPrevious) {
            e.preventDefault();
            onPrevious();
        }
    };

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [hasNext, hasPrevious]);

    const minSwipeDistance = 50;

    const onTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientY);
    };

    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientY);

        if (!touchStart) return;

        const distance = touchStart - e.targetTouches[0].clientY;
        const absDistance = Math.abs(distance);

        // Prevent default scroll behavior when swiping vertically
        if (absDistance > 10) {
            e.preventDefault();
        }

        if (absDistance > minSwipeDistance) {
            if (distance > 0 && hasNext) {
                setSwipeIndicator({ visible: true, direction: 'up' });
            } else if (distance < 0 && hasPrevious) {
                setSwipeIndicator({ visible: true, direction: 'down' });
            } else {
                setSwipeIndicator({ visible: false, direction: null });
            }
        } else {
            setSwipeIndicator({ visible: false, direction: null });
        }
    };

    const onTouchEnd = (e) => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isUpSwipe = distance > minSwipeDistance;
        const isDownSwipe = distance < -minSwipeDistance;

        setSwipeIndicator({ visible: false, direction: null });

        if (isUpSwipe && hasNext) {
            e.preventDefault();
            onNext();
        } else if (isDownSwipe && hasPrevious) {
            e.preventDefault();
            onPrevious();
        }

        setTouchStart(null);
        setTouchEnd(null);
    };

    if (!linkId) return null;

    return (
        <ModalOverlay onClick={handleOverlayClick}>
            <ModalContent
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
            >
                <CloseButton onClick={onClose}>&times;</CloseButton>

                <SwipeIndicator visible={swipeIndicator.visible} direction={swipeIndicator.direction}>
                    {swipeIndicator.direction === 'up' ? '↑ Next Video' : '↓ Previous Video'}
                </SwipeIndicator>

                {isLoading ? (
                    <LoadingText>Loading video...</LoadingText>
                ) : fileData ? (
                    <VideoContainer>
                        <StyledVideo
                            controls
                            autoPlay
                            playsInline
                            webkit-playsinline="true"
                            key={linkId}
                        >
                            <source
                                src={`${BASE_URL}/public/files/links/${linkId}`}
                                type={fileData.contentType}
                            />
                            Your browser does not support video playback.
                        </StyledVideo>
                    </VideoContainer>
                ) : (
                    <LoadingText>Failed to load video.</LoadingText>
                )}
            </ModalContent>
        </ModalOverlay>
    );
};

export default VideoPlayerModal;
